import {
  WasteManifestSource as PrismaWasteManifestSource,
  WasteManifestIngestSource,
  RcraInfoFederalWasteGeneratorStatus,
  StateRegulatoryStatus,
} from "encamp-prisma/generated/browser";

import { prettyPrintEnumValue } from "../utils/prettyPrintEnumValue";
import { uuidv5 } from "../utils/uuid";
import { FacilityRegulatoryStatus } from "./types";

export {
  MANIFEST_REVIEW_DISCREPANCY_FIELDS,
  type ManifestReviewDiscrepancyField,
} from "./manifestReviewDiscrepancyFields";

export function getManifestId({
  manifestTrackingNumber,
  tenantId,
  manifestSource,
  ingestSource,
  vendorSource,
}: {
  manifestTrackingNumber: string;
  tenantId: string;
  manifestSource: PrismaWasteManifestSource;
  ingestSource: WasteManifestIngestSource;
  vendorSource?: string | null;
}): string {
  // Always include vendorSource to match the unified unique constraint
  // For eManifests: vendorSource is null, for vendor manifests: vendorSource is the vendor name
  const vendorPart = vendorSource || "null";
  const manifestId = `${manifestTrackingNumber}-${tenantId}-${manifestSource}-${ingestSource}-${vendorPart}`;

  return uuidv5(manifestId);
}

export const federalWasteGeneratorStatusToLabel = (
  status: RcraInfoFederalWasteGeneratorStatus | null
) => {
  switch (status) {
    case RcraInfoFederalWasteGeneratorStatus.VERY_SMALL_QUANTITY_GENERATOR:
      return "VSQG";
    case RcraInfoFederalWasteGeneratorStatus.SMALL_QUANTITY_GENERATOR:
      return "SQG";
    case RcraInfoFederalWasteGeneratorStatus.LARGE_QUANTITY_GENERATOR:
      return "LQG";
    case RcraInfoFederalWasteGeneratorStatus.NOT_A_GENERATOR:
      return "Not a Generator";
    case RcraInfoFederalWasteGeneratorStatus.UNDETERMINED:
    case null:
      return "None";
    default:
      return prettyPrintEnumValue(status);
  }
};

/**
 * Long display labels for waste generator statuses (selects, recommendation
 * captions, EPA-reported display rows, dashboard tiles). Keyed by the raw enum
 * string so the value-identical federal and state generator-status enums share
 * one map. For the short forms (e.g. "LQG") use
 * `federalWasteGeneratorStatusToLabel` / `stateWasteGeneratorStatusToLabel`.
 */
const GENERATOR_STATUS_LONG_LABELS: Record<string, string> = {
  LARGE_QUANTITY_GENERATOR: "Large Quantity Generator (LQG)",
  SMALL_QUANTITY_GENERATOR: "Small Quantity Generator (SQG)",
  VERY_SMALL_QUANTITY_GENERATOR: "Very Small Quantity Generator (VSQG)",
  NOT_A_GENERATOR: "Not a Generator",
  UNDETERMINED: "Undetermined",
  PENDING: "Pending",
  CONDITIONALLY_EXEMPT_SMALL_QUANTITY_GENERATOR:
    "Conditionally Exempt Small Quantity Generator (CESQG)",
  KANSAS_SMALL_QUANTITY_GENERATOR: "Kansas Small Quantity Generator",
  MARYLAND_GENERATOR_FULLY_REGULATED: "Maryland Generator Fully Regulated",
  MEDIUM_QUANTITY_GENERATOR: "Medium Quantity Generator (MQG)",
  TEXAS_CLASS_1_ONLY_GENERATOR: "Texas Class 1 Only Generator",
  MINI_QUANTITY_GENERATOR: "Mini Quantity Generator",
  SAME_AS_FEDERAL: "Same as Federal",
};

export function wasteGeneratorStatusToLongLabel(
  status: RcraInfoFederalWasteGeneratorStatus | StateRegulatoryStatus
): string {
  return GENERATOR_STATUS_LONG_LABELS[status] ?? prettyPrintEnumValue(status);
}

export const stateWasteGeneratorStatusToLabel = (status: StateRegulatoryStatus | null) => {
  switch (status) {
    case null:
      return "None";
    case StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR:
      return "VSQG";
    case StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR:
      return "SQG";
    case StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR:
      return "LQG";
    case StateRegulatoryStatus.CONDITIONALLY_EXEMPT_SMALL_QUANTITY_GENERATOR:
      return "CESQG";
    case StateRegulatoryStatus.KANSAS_SMALL_QUANTITY_GENERATOR:
      return "KSQG";
    case StateRegulatoryStatus.MARYLAND_GENERATOR_FULLY_REGULATED:
      return "MD Gen (FR)";
    case StateRegulatoryStatus.MEDIUM_QUANTITY_GENERATOR:
      return "MQG";
    case StateRegulatoryStatus.TEXAS_CLASS_1_ONLY_GENERATOR:
      return "Class 1 Only Gen";
    case StateRegulatoryStatus.MINI_QUANTITY_GENERATOR:
      return "MiniQG";
    case StateRegulatoryStatus.SAME_AS_FEDERAL:
      return "Same as Federal";
    default:
      return prettyPrintEnumValue(status);
  }
};

/**
 * State generator-status rank (higher number = higher status), following the
 * state status hierarchy (LQG highest … MiniQG lowest). A facility is in a
 * single state, so its statuses share one category system and rank cleanly.
 * Anything that isn't a concrete tier — SAME_AS_FEDERAL, NOT_A_GENERATOR,
 * PENDING, UNDETERMINED, null — ranks 0.
 *
 * Federal LQG/SQG/VSQG share these keys, so a reported federal status can be
 * ranked here. That started as the narrow "same as federal" case, but
 * `effectiveReportedStateStatus` now routes ANY non-tier reported state status
 * (including "not a generator" — nearly every TX handler) through the reported
 * federal value, so federal statuses ranking here is a large real population.
 * Every concrete federal tier must remain a key in this map, or facilities
 * reporting that tier would rank 0 and flag monthly state exceedances.
 * Takes a raw enum value string so callers on either the Prisma or GraphQL
 * `StateRegulatoryStatus` enum share one table. The SQL view and CTE ranking
 * mirror this map.
 */
const STATE_GENERATOR_STATUS_RANK: Record<string, number> = {
  LARGE_QUANTITY_GENERATOR: 9,
  MARYLAND_GENERATOR_FULLY_REGULATED: 8,
  MEDIUM_QUANTITY_GENERATOR: 7,
  SMALL_QUANTITY_GENERATOR: 6,
  VERY_SMALL_QUANTITY_GENERATOR: 5,
  KANSAS_SMALL_QUANTITY_GENERATOR: 4,
  TEXAS_CLASS_1_ONLY_GENERATOR: 3,
  CONDITIONALLY_EXEMPT_SMALL_QUANTITY_GENERATOR: 2,
  MINI_QUANTITY_GENERATOR: 1,
};

export const stateGeneratorStatusRank = (status: string | null): number =>
  status ? (STATE_GENERATOR_STATUS_RANK[status] ?? 0) : 0;

/**
 * The reported status a calculated state generator status should be compared
 * against. When the reported state status is a concrete tier, compare against
 * it directly. When it carries no tier — "same as federal", "not a generator",
 * pending, or nothing reported at all — compare against the reported FEDERAL
 * status instead: comparing against the rank-0 value would flag every month
 * with any calculated state status as an exceedance (e.g. TX reports state
 * code N for nearly all handlers, so federal-LQG sites flagged monthly). A
 * calculated state status that outranks the reported federal status still
 * counts as an exceedance.
 */
export const effectiveReportedStateStatus = (
  reportedStateStatus: string | null,
  reportedFederalStatus: string | null
): string | null =>
  stateGeneratorStatusRank(reportedStateStatus) > 0 ? reportedStateStatus : reportedFederalStatus;

export function regulatoryStatusesDiscrepanceExists(
  ...statuses: RcraInfoFederalWasteGeneratorStatus[]
) {
  // For the purposes of comparison, treat Undetermined and NotAGenerator as the same
  const normalizedStatuses = statuses.map((status) =>
    status === RcraInfoFederalWasteGeneratorStatus.UNDETERMINED
      ? RcraInfoFederalWasteGeneratorStatus.NOT_A_GENERATOR
      : status
  );

  return new Set(normalizedStatuses).size > 1;
}

export function getRegulatoryStatusDiscrepancies(regulatoryStatus: FacilityRegulatoryStatus) {
  const {
    rcraInfo,
    eManifestHighestStatusThisYear: eManifest,
    vendorHighestStatusThisYear: vendor,
  } = regulatoryStatus;

  const discrepancies: string[] = [];

  if (regulatoryStatusesDiscrepanceExists(rcraInfo, eManifest)) {
    discrepancies.push(
      `There is a discrepancy between the EPA current status and the generator status calculated from eManifest data.\n    EPA: ${federalWasteGeneratorStatusToLabel(
        rcraInfo
      )}\n    eManifest: ${federalWasteGeneratorStatusToLabel(eManifest)}`
    );
  }

  if (regulatoryStatusesDiscrepanceExists(rcraInfo, vendor)) {
    discrepancies.push(
      `There is a discrepancy between the EPA current status and the generator status calculated from vendor data.\n    EPA: ${federalWasteGeneratorStatusToLabel(
        rcraInfo
      )}\n    Vendor: ${federalWasteGeneratorStatusToLabel(vendor)}`
    );
  }

  if (regulatoryStatusesDiscrepanceExists(eManifest, vendor)) {
    discrepancies.push(
      `There is a discrepancy between the generator statuses calculated from eManifest data and from vendor data.\n    eManifest: ${federalWasteGeneratorStatusToLabel(
        eManifest
      )}\n    Vendor: ${federalWasteGeneratorStatusToLabel(vendor)}`
    );
  }

  return discrepancies;
}

export function getRegulatoryStatusIssueDetails(regulatoryStatus: FacilityRegulatoryStatus) {
  const {
    rcraInfo,
    eManifestHighestStatusThisYear: eManifest,
    vendorHighestStatusThisYear: vendor,
  } = regulatoryStatus;

  if (regulatoryStatusesDiscrepanceExists(rcraInfo, eManifest, vendor)) {
    return `EPA: ${federalWasteGeneratorStatusToLabel(rcraInfo)}
e-Manifest: ${federalWasteGeneratorStatusToLabel(eManifest)}
Vendor: ${federalWasteGeneratorStatusToLabel(vendor)}`;
  }

  return null;
}
export function getRegulatoryStatusIssueSummary(regulatoryStatus: FacilityRegulatoryStatus) {
  const {
    rcraInfo,
    eManifestHighestStatusThisYear: eManifest,
    vendorHighestStatusThisYear: vendor,
  } = regulatoryStatus;

  if (regulatoryStatusesDiscrepanceExists(rcraInfo, eManifest, vendor)) {
    return `There is a discrepancy between the EPA current status and the generator status calculated from manifest and vendor data.

    EPA: ${federalWasteGeneratorStatusToLabel(rcraInfo)}
    e-Manifest: ${federalWasteGeneratorStatusToLabel(eManifest)}
    Vendor: ${federalWasteGeneratorStatusToLabel(vendor)}`;
  }

  return null;
}

export {
  VALID_SOURCE_CODES,
  VALID_FORM_CODES,
  VALID_MANAGEMENT_METHOD_CODES,
  EXCLUDED_ONSITE_MANAGEMENT_METHODS,
  SOURCE_CODES_REQUIRING_COMMENT,
  PHRM_CODES,
  isValidSourceCode,
  isValidFormCode,
  isValidManagementMethodCode,
  isExcludedOnsiteManagementMethod,
  SPLIT_BY_MMC_FORM_CODES,
  isSplitByManagementMethod,
  isValidStandardFederalWasteCode,
  isPhrmCode,
  isOutsCode,
  isExportableFederalCode,
  isExportableStateCode,
  type ExportableFederalCodeContext,
  type SourceCode,
  type FormCode,
  type ManagementMethodCode,
  type ExcludedOnsiteManagementMethod,
} from "./validCodes";

export {
  STATE_WASTE_CODES,
  stateHasWasteCodes,
  isValidStateWasteCode,
  isSubjectToEManifest,
  getStateWasteCodeEntry,
  getStateWasteCodeClassification,
  classifyStateWasteCode,
  getStateWasteCodes,
  type StateWasteClassification,
  type StateWasteCodeEntry,
} from "./stateWasteCodes";

export { STATES_WITHOUT_OI_FORMS, stateRequiresOiForms } from "./constants";

export { SOURCE_CODE_DESCRIPTIONS, getSourceCodeDescription } from "./sourceCodeDescriptions";

export {
  coalesceWasteStreamRawCodes,
  type WasteLineCodeSource,
  type CoalescedWasteStreamRawCodes,
} from "./coalesceWasteStreamRawCodes";

export { stripDotShippingDetails } from "./stripDotShippingDetails";

export { isEpaIdSiteInactive } from "./epaIdActiveStatus";

export { generateWasteStreamName, type WasteStreamNameSource } from "./generateWasteStreamName";

export {
  WasteManifestSource,
  MANIFEST_SOURCE_DISPLAY_PRIORITY,
  getAvailableManifestSources,
  getPrimaryManifestSource,
  pickManifestFieldFromPrimary,
  selectClassificationCodes,
  isManifestDocumentReviewed,
  compareByTransporterLineNumber,
  type ManifestSourceInputs,
  type ManifestSourceFieldValues,
  type ManifestDocumentReviewState,
} from "./manifestSourcePriority";

export {
  calculateWasteLineHazAndAcuteQuantities,
  formatManifestTrackingNumber,
  getExistingManifests,
  getManifestKey,
  processTagsInTransaction,
  processWasteBatch,
  type LinkedWasteRecords,
} from "./import";
